/**
 * The sign-out and token-write side of authentication, seen from the side that
 * drives it: the two logout sequences, the three teardown arms they call
 * (server, browser extension, Microsoft SSO), the react-query / persisted-model
 * wipe, and the three writers that put a token or a provider cookie in place.
 *
 * These nine functions were the nine effects of the `auth` Rematch model. Every
 * body below is the effect body it came from, carried over with ONE mechanical
 * rewrite and nothing else: a `dispatch.<slice>.<method>` call is now
 * `store.dispatch.<slice>.<method>`, because a plain function has no model
 * dispatch in scope. Ordering, awaiting, branch conditions, log strings and
 * comments are untouched, so a reviewer diffing a function against the effect it
 * replaced finds only that prefix.
 *
 * The state these functions read and write still belongs to the `auth` slice —
 * `token` and `authProvider` are read through `store.getState()`, and the writes
 * go through the slice's own reducers (`clear`, `_setToken`, `_setAuthProvider`)
 * — so this module imports the store singleton and is carried on the containment
 * baseline rather than off it. That is the honest shape of this seam today:
 * orchestration over a slice's state, not state of its own. It moves off the
 * baseline when the slice's state moves to atoms.
 *
 * The nine effects remain on the model as one-line delegates rather than being
 * deleted, and that is load-bearing rather than transitional tidiness. The
 * Rematch loading plugin derives one loading flag per declared effect, keyed by
 * model and effect name. Delete the effect, or repoint a caller past it, and
 * that flag never flips again — it reads `undefined` forever. Keeping the
 * delegates keeps every caller's observable behaviour, the loading flags
 * included, exactly where it was.
 *
 * `logout` is the one function here that no longer *depends* on that flag. The
 * question "is an intentional logout in flight?" — asked at eleven sites across
 * six production files, to suppress a second, session-expiry logout — is now
 * answered by `@/lib/authInFlight`, which `logout` enters at the top and exits
 * in a `finally` covering every arm. Entering and exiting rather than setting
 * and clearing is what keeps two overlapping logouts behaving as they did on
 * the loading plugin's per-effect counter: the signal stays true until the last
 * one leaves. The Rematch flag is still derived and still correct for
 * `dispatch.auth.logout()` callers; it is simply no longer the only place the
 * answer lives, which is what unblocks repointing those callers at this
 * function.
 *
 * This file sits at `src/lib/` rather than `src/lib/auth/` deliberately.
 * `src/lib/auth/` is the token-resolution chain — `getAccessToken`,
 * `readFromProviderSourceOfTruth`, `effectiveAuthProvider` — a set that is read
 * and reviewed as a unit; a logout orchestrator is not part of it. The sibling
 * `src/lib/chunkAudioActions.ts` is the same shape at the same level.
 *
 * Nothing runs at module scope: the imports, one type alias and one constant are
 * the whole of this module's evaluation, and every store access sits inside a
 * function body. That is what keeps the `authModel` → here → `@/store` import
 * edge from becoming an initialization-order dependency, which on this path is
 * not hypothetical — `authModel.ts` is on one of three documented static-cycle
 * chains in this tree, each of which surfaced only as a "cannot access before
 * initialization" in a production build.
 */
import { clearEhrLaunchRegion } from '@/atoms/ehrLaunchRegionAtom';
import {
  clearExternalLaunchAuth,
  externalLaunchContextAtom,
  ExternalLaunchType,
} from '@/atoms/externalLaunchAuthAtoms';
import { IS_SSO_MICROSOFT_LOGOUT_ENABLED } from '@/components/constants/flags';
import { clearPartnerSlug } from '@/context/white-label';
import { POST_LOGOUT_REDIRECT_COOKIE } from '@/hooks/usePostLogoutRedirect';
import { clearPersistedMdAuthToken } from '@/integrations/medical-director/medicalDirectorSidebar';
import { resetIdentity } from '@/lib/analytics/scribe/auth';
import { AUTH_PROVIDER_COOKIE } from '@/lib/auth-constants';
import { clearAuth0Cookies } from '@/lib/auth0/cookies';
import { enterLogoutInFlight, exitLogoutInFlight } from '@/lib/authInFlight';
import { shutDownIntercom } from '@/lib/Intercom';
import { logoutChannel } from '@/lib/logoutChannel';
import { shutDownPyne } from '@/lib/Pyne';
import { clearSnowplowUserData } from '@/lib/snowplow';
import { logoutFromServer } from '@/pages/api/scribeAPI';
import { resetAmplitude } from '@/providers/amplitude-provider';
import { invalidatePosthogFlags } from '@/providers/posthog-flags';
import { store } from '@/store';
import { recorderFmc } from '@/tauri/controller/recording';
import { sweepCaptureOnSessionEnd } from '@/tauri/online-chunk-upload/session-end-sweep';
import { isTauri } from '@/tauri/utils';
import { resetHardwareProfileMetadataCache } from '@/utils/analytics/hardware-profile-metadata';
import { authProviderCookieIdentity } from '@/utils/auth/authProviderCookie';
import { clearLogoutStorage } from '@/utils/auth/logoutStorage';
import { AuthProvider } from '@/utils/auth/persistedAuthProvider';
import { stripLocalePathPrefix } from '@/utils/i18n/supportedLocales';
import { bridgeErrorKind } from '@/utils/logger/bridge-error-kind';
import { logger } from '@/utils/logger/logger';
import { navigateToOnPremDesktopRecovery } from '@/utils/onpremDesktopRecovery';
import { PUBLIC_ROUTES } from '@/utils/publicRoutes';
import { reactQueryClient } from '@/utils/reactQueryClient';
import { resetLogoutCooldown } from '@/utils/scribeHTTP';
import { logoutClearAudio, type AudioWipeReason } from '@heidi/capture/db';
import { markSccAccessDenied } from '@heidi/coding/data';
import { getCookie, setCookie } from 'cookies-next/client';
import { getDefaultStore } from 'jotai';
import Router from 'next/router';
import { isEdge } from 'react-device-detect';
import { v4 as uuid } from 'uuid';

// `clearAuthCache` is imported lazily inside the functions below to avoid
// a static-time circular: getAccessToken.ts → @/store → this file →
// @/lib/auth (re-export of getAccessToken). Lazy import resolves at
// call time, after both modules are fully initialised.
async function clearAuthCacheForLogout(): Promise<void> {
  const { clearAuthCache } = await import('@/lib/auth');
  clearAuthCache({ reason: 'logout' });
}

async function clearActiveNoteGenerationsForLogout(): Promise<void> {
  const [{ stopAllSessionGenerations }, { clearAllSideBySideSessionStates }] =
    await Promise.all([
      import('@/features/note-generation/generation-coordinator'),
      import('@/features/sideBySideNoteGen/atoms/atoms'),
    ]);
  stopAllSessionGenerations('cleanup');
  clearAllSideBySideSessionStates();
}

async function stopComputerUseForLogout(): Promise<void> {
  if (!isTauri()) return;
  try {
    const { stopComputerUseOnLogout } =
      await import('@/features/rpa/agent-client/cancel-computer-use-run');
    await stopComputerUseOnLogout();
  } catch (error) {
    logger.warn('[authActions.logout] Computer Use stop could not run', {
      errorKind: bridgeErrorKind(error),
    });
  }
}

/**
 * The `logout` payload, named rather than inline so the model's delegate can
 * declare the identical dispatch signature. The members are unchanged.
 */
export type LogoutOptions = {
  postLogoutRedirectURL?: string;
  force?: boolean;
  audioWipeId?: string;
  audioWipeReason?: AudioWipeReason;
};

const AUTH_PROVIDER_COOKIE_MAX_AGE_SECONDS = 60 * 60 * 24 * 30; // 30 days

// Declared under a distinct local name and exported under the effect's name:
// the body calls `logoutFromServer()` meaning the scribeAPI import, and an
// exported binding of that name at module scope would shadow it. On the model
// this never arose — the effect was a property of an object literal, not a
// module-scope binding — so the alias is what keeps the body byte-identical.
async function logoutFromServerEffect() {
  try {
    // call this only if we have a token, otherwise we get a 401
    const kindeToken = store.getState().auth?.token;
    if (kindeToken) {
      await logoutFromServer();
    }
  } catch (error) {
    logger.error('Error logging out from server', error);
  }
}

export { logoutFromServerEffect as logoutFromServer };

export function ssoLogout() {
  if (!isTauri()) {
    try {
      if (IS_SSO_MICROSOFT_LOGOUT_ENABLED()) {
        window.open('https://login.microsoftonline.com/common/oauth2/logout');
      }
    } catch (error) {
      logger.error('Error getting feature flags', error);
      if (IS_SSO_MICROSOFT_LOGOUT_ENABLED()) {
        window.open('https://login.microsoftonline.com/common/oauth2/logout');
      }
    }
  }
}

export function logoutFromExtension() {
  if (typeof chrome === 'undefined' || !chrome.runtime) {
    return;
  }
  const data = {
    type: 'HEIDI_WEB_APP_LOGOUT',
  };

  try {
    void chrome.runtime.sendMessage(
      isEdge
        ? process.env.NEXT_PUBLIC_EDGE_EXTENSION_ID
        : process.env.NEXT_PUBLIC_CHROME_EXTENSION_ID,
      data
    );

    store.dispatch.analyticsScribe.extensionSetToken();
  } catch (error) {
    logger.error('Error logging out from extension', error);
  }
}

export async function logout({
  postLogoutRedirectURL,
  force,
  audioWipeId,
  audioWipeReason,
}: LogoutOptions = {}) {
  enterLogoutInFlight();
  try {
    const pathname = stripLocalePathPrefix(window.location.pathname);
    const isPublicRoute = PUBLIC_ROUTES.some((route) => pathname.startsWith(route));
    // FR-038: the only recorder touch in this file is
    // `clear_audio_file_directory()`, which deletes files and does not stop
    // capture — so a logout has been leaving native capture and chunk upload
    // running against the session the user just walked away from.
    // `anyActiveUpload` because the user's involvement with any live recording
    // ends here. Awaited before auth teardown, and safe to ignore: the helper
    // never throws and a wedged native stop must not strand the user signed in.
    //
    // It sits ABOVE the public-route early return, not below it: that return
    // ends the session too, and it is the path `debouncedExpiryLogout` takes —
    // the one logout the clinician did not initiate, and so precisely the one
    // where a runaway capture goes unnoticed. Below the return, a non-forced
    // logout on a public route would end the session and never sweep. Moving it
    // up does not widen the sweep: `logout` is still the only call site, and a
    // logout on a public route is still a session ending.
    await Promise.all([
      sweepCaptureOnSessionEnd({
        target: { kind: 'anyActiveUpload' },
        callSite: 'logout',
      }),
      stopComputerUseForLogout(),
    ]);
    // Server-authoritative invalid-session triggers (e.g.
    // `debouncedExpiryLogout` for `session_invalid_query`) must still
    // clean stale cookies / IndexedDB / persisted models / analytics
    // identity even on public routes. `force: true` bypasses the early
    // return; the redirect at the end is then skipped for public
    // routes to avoid unwanted navigation.
    if (isPublicRoute && !force) {
      return;
    }
    const authProvider = store.getState().auth?.authProvider;
    const cookieProvider = getCookie(AUTH_PROVIDER_COOKIE);
    if ((authProvider as string | undefined) !== cookieProvider) {
      logger.error('Auth provider mismatch between Redux state and cookie', {
        redux: authProvider,
        cookie: cookieProvider,
      });
    }
    await clearActiveNoteGenerationsForLogout();
    const resolvedAudioWipeId = audioWipeId ?? uuid();
    if (!isTauri()) await logoutClearAudio(resolvedAudioWipeId, audioWipeReason);
    store.dispatch.auth.logoutFromExtension();
    store.dispatch.auth.ssoLogout();
    await store.dispatch.auth.logoutFromServer();
    // MD-only: clear the Sidebar.Settings copy of the token so the widget
    // doesn't rehydrate it on next launch. No-op outside the MD WebView2.
    clearPersistedMdAuthToken();
    // Clear the auth token cache with an explicit-logout reason so the
    // session-invalidation telemetry subscriber suppresses its
    // `debouncedExpiryLogout` call (only fires on `'session_invalid'`,
    // not on intentional logout). Must precede `dispatch.auth.clear()`
    // — the Redux clear triggers cross-tab logout listeners that
    // expect the cache to be cleared first.
    await clearAuthCacheForLogout();
    store.dispatch.auth.clear();
    markSccAccessDenied();
    clearExternalLaunchAuth();
    // Clear auth provider cookie to prevent stale auth state on next visit
    clearAuth0Cookies();
    // Close sidebar so next login starts with a clean view
    localStorage.removeItem('heidi-sidebar-expanded');
    clearLogoutStorage();
    // Clear white-label partner branding on logout
    clearPartnerSlug();

    invalidatePosthogFlags();
    resetAmplitude();
    clearSnowplowUserData();
    // Re-send the linked Remote's serial / firmware to the next identity that
    // signs in on this install, instead of suppressing it as unchanged.
    resetHardwareProfileMetadataCache();
    // Segment parity with the resets above — a stale ajs_user_id surviving
    // logout mints anonymous Braze profiles on logged-out pages; see
    // analyticsScribe.resetIdentity.
    resetIdentity();
    resetLogoutCooldown();
    await store.dispatch.auth.clearPersistedModels();

    void shutDownIntercom();
    void shutDownPyne();
    void logoutChannel.postMessage({
      logout: true,
      audioWipeId: resolvedAudioWipeId,
    });

    try {
      localStorage.removeItem('ajs_user_traits');
    } catch {
      // ignore
    }
    if (!isTauri()) {
      if (postLogoutRedirectURL) {
        setCookie(POST_LOGOUT_REDIRECT_COOKIE, postLogoutRedirectURL, {
          path: '/',
        });
      }
    }

    // Forced cleanup on a public route: do not navigate away.
    if (isPublicRoute && force) {
      return;
    }

    // Handle logout redirect based on auth provider and platform
    if (isTauri()) {
      if (authProvider === AuthProvider.AUTH0_DESKTOP) {
        logger.debug('[authModel.logout] redirecting to desktop-logout page');
        await Router.push('/desktop-logout');
        return;
      }
      if (authProvider === AuthProvider.KEYCLOAK) {
        // Tauri/WebView2 cannot recover once Keycloak renders an
        // invalid post_logout_redirect_uri page. Desktop logout is
        // therefore local and app-owned: clear the stored OIDC user,
        // force the next sign-in to show the login prompt, then land on
        // an explicit bundled recovery route. Routing to "/" is not
        // reliable in the static Tauri shell once the root/auth
        // bootstrap is already broken.
        logger.info('[authModel.logout] Desktop Keycloak — local session cleanup');
        const { clearKeycloakTauriSession } = await import('@/tauri/keycloak/oidcClient');
        try {
          await clearKeycloakTauriSession({ requireFreshLogin: true });
        } catch (err) {
          logger.error(
            '[authModel.logout] clearKeycloakTauriSession rejected; continuing to recovery route',
            err
          );
        }
        navigateToOnPremDesktopRecovery('logout');
        return;
      }
      // TODO desktop: add logic to logout from Auth0
      // Tauri with Kinde
      logger.info('Desktop logging out from Kinde');
      await Router.push('/kinde-logout');
      return;
    }

    if (authProvider === AuthProvider.AUTH0) {
      logger.info('Logging out from Auth0');
      await Router.push(`/api/auth0/logout`);
      return;
    }

    if (authProvider === AuthProvider.KEYCLOAK) {
      // RP-initiated logout: clear the HttpOnly NextAuth cookie + chain
      // through Keycloak's end_session_endpoint so the SSO session is
      // also invalidated. Without that chain the Keycloak SSO cookie
      // survives and the next sign-in skips the login form entirely.
      // Dynamic import keeps these modules out of cloud bundles.
      const { performKeycloakLogout } = await import('@/utils/keycloakLogout');
      await performKeycloakLogout();
      return;
    }

    logger.info('Logging out from Kinde');
    await Router.push('/api/auth/logout');
  } finally {
    exitLogoutInFlight();
  }
}

export async function logoutWithoutRedirect() {
  enterLogoutInFlight();
  try {
    // FR-038: same teardown as `logout` — see the comment there. This variant
    // skips the redirect, not the sign-out, so it leaves the same live recording
    // behind if nothing stops it.
    await Promise.all([
      sweepCaptureOnSessionEnd({
        target: { kind: 'anyActiveUpload' },
        callSite: 'logoutWithoutRedirect',
      }),
      stopComputerUseForLogout(),
    ]);
    if (!isTauri()) await logoutClearAudio(uuid());
    await store.dispatch.auth.logoutFromServer();
    // MD-only: clear the Sidebar.Settings copy of the token. No-op outside
    // the MD WebView2.
    clearPersistedMdAuthToken();
    // Same cache-clear pattern as the main logout effect — see note
    // there. The redirect-less variant still counts as intentional
    // logout for the telemetry subscriber.
    await clearAuthCacheForLogout();
    store.dispatch.auth.clear();
    markSccAccessDenied();
    clearExternalLaunchAuth();
    // Clear auth provider cookie to prevent stale auth state on next visit
    clearAuth0Cookies();
    // Close sidebar so next login starts with a clean view
    localStorage.removeItem('heidi-sidebar-expanded');
    clearLogoutStorage();
    invalidatePosthogFlags();
    resetAmplitude();
    clearSnowplowUserData();
    resetHardwareProfileMetadataCache();
    // Segment parity — see the comment in `logout` above.
    resetIdentity();
    await store.dispatch.auth.clearPersistedModels();
    void shutDownPyne();
  } finally {
    exitLogoutInFlight();
  }
}

export async function clearPersistedModels() {
  // EHR-launched tabs run on /external/* routes and must not have their auth
  // wiped by stale-session cleanup — the FHIR token lives in Jotai atoms, not
  // Redux, so Kinde/Auth0 correctly report logged-out and would trigger this.
  const isGenericSmartOnFhirLaunch =
    getDefaultStore().get(externalLaunchContextAtom)?.launchType ===
    ExternalLaunchType.GENERIC_SMART_ON_FHIR;
  if (
    typeof window !== 'undefined' &&
    window.location.pathname.includes('/external/') &&
    isGenericSmartOnFhirLaunch
  ) {
    return;
  }

  // Keep web audio for re-auth; Tauri clears its native directory below.
  // queryClient.removeQueries will remove data from the cache, but does not inform observers, so if they are rendered on the screen, it will stay there.
  // This is a good function to use when you logout and go to the login screen and remove all queries for the user.
  await reactQueryClient.cancelQueries();
  reactQueryClient.removeQueries();

  localStorage.removeItem('REACT_QUERY_OFFLINE_CACHE');

  // `clearPersistedModels` is reachable from non-logout flows
  // (logout retry, persisted-model reset) but always implies the
  // user is being signed out from the cache's perspective, so flag
  // with `reason: 'logout'`. Idempotent if a higher-level logout
  // flow already called it.
  await clearAuthCacheForLogout();
  store.dispatch.auth.clear();
  // The EHR-launch region carrier took over from `auth.userRegion` when that
  // field stopped being persisted, so it dies wherever that field died. This is
  // the one `auth.clear()` the storage sweep does not cover: `clearLogoutStorage`
  // is deliberately not called on stale-session cleanup. The early return above
  // still protects a live generic SMART-on-FHIR tab, exactly as it protects the
  // slice clear on the line before.
  clearEhrLaunchRegion();
  clearExternalLaunchAuth();
  reactQueryClient.clear();
  if (isTauri()) {
    void recorderFmc.clear_audio_file_directory();
  }
}

export function setToken(token: string) {
  store.dispatch.auth._setToken(token);
}

/*
 * This sets a non-persistent auth provider cookie and should only be used by the integrations team during various authentication flows
 * (Unless there is a good reason to use this)
 */
export function setAuthProviderAsSessionCookie(authProvider: AuthProvider) {
  setCookie(AUTH_PROVIDER_COOKIE, authProvider, {
    ...authProviderCookieIdentity,
    sameSite: 'none',
    secure: true,
  });
  store.dispatch.auth._setAuthProvider(authProvider);
}

export function setAuthProviderAsPersistentCookie(authProvider: AuthProvider) {
  setCookie(AUTH_PROVIDER_COOKIE, authProvider, {
    ...authProviderCookieIdentity,
    sameSite: 'none',
    secure: true,
    maxAge: AUTH_PROVIDER_COOKIE_MAX_AGE_SECONDS,
  });
  store.dispatch.auth._setAuthProvider(authProvider);
}
